import { Stack } from '@mui/material';
import { useScrollToFirstErroredInput } from 'hooks/useScrollToFirstErroredInput';
import { castArray, omit } from 'lodash-es';
import { forwardRef, memo, useEffect, useImperativeHandle } from 'react';
import { SkeletonList } from '../SkeletonList';
import { RenderFieldWithWrapper } from './QuestionEngine.components';
import type {
  Field,
  QuestionEngineProps,
  QuestionEngineRef,
  QuestionEngineStateValues,
} from './QuestionEngine.types';
import { handleShowCondition } from './QuestionEngine.utils';

const QuestionEngine = <T,>(
  props: QuestionEngineProps<T extends QuestionEngineStateValues ? QuestionEngineStateValues : any>,
  ref: React.Ref<QuestionEngineRef | undefined>,
) => {
  const {
    fields,
    sx,
    state = {},
    formik,
    isReadOnly,
    setState,
    showLoader = false,
    wrapperSx,
    onChangeEmitted,
    insertAfterField,
    readonlyFieldCodes,
  } = props;

  const nonVisibleFieldCodes: string[] = [];

  const visibleFields =
    fields?.reduce((acc, field) => {
      if (handleShowCondition(field, state) && !field?.is_hidden) {
        acc.push(field);
      } else {
        nonVisibleFieldCodes.push(field.code);
      }

      return acc;
    }, [] as Field[]) || [];

  // Remove non-visible fields from state if they changed
  useEffect(() => {
    setState?.((prevState) => omit(prevState, nonVisibleFieldCodes));
    formik?.setValues(omit(formik?.values, nonVisibleFieldCodes));
  }, [nonVisibleFieldCodes.join('|')]);

  useImperativeHandle(ref, () => ({
    renderedQuestions: () => visibleFields,
  }));

  useScrollToFirstErroredInput({ formik });

  if (showLoader) {
    return (
      <Stack data-test="loading" sx={[...castArray(wrapperSx)]}>
        <SkeletonList count={7} />
      </Stack>
    );
  }

  return (
    <Stack sx={[...castArray(wrapperSx)]}>
      {visibleFields?.flatMap((field, idx) => {
        const key = (field?.code || '') + (field?.type || '');
        const isFieldReadOnly = isReadOnly || (readonlyFieldCodes?.includes(field.code) ?? false);

        const elements = [
          <RenderFieldWithWrapper
            field={field}
            sx={sx}
            key={key}
            formik={formik}
            state={state}
            setState={setState}
            isReadOnly={isFieldReadOnly}
            idx={idx}
            onChangeEmitted={onChangeEmitted}
          />,
        ];

        if (insertAfterField?.code === field.code) {
          elements.push(
            <Stack key={`insert-after-${field.code}`} sx={{ mt: 2 }}>
              {insertAfterField.node}
            </Stack>,
          );
        }

        return elements;
      })}
    </Stack>
  );
};

export default memo(forwardRef(QuestionEngine));
